import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, Button, Typography } from 'antd';
import cn from 'classnames';

import {
	NoResult,
	NotificationCard,
	useNotificationsListQuery,
	useNotificationsUnreadCountRetrieveQuery,
	useReadNotifications,
} from 'entities/Notification';
import { useCommonSlice } from 'shared/hooks';
import { EMainRoutes } from 'shared/typings';
import { OverlayLoader } from 'shared/ui';

import styles from './NotificationsPopup.module.scss';

interface INotificationsPopupProps extends HTMLAttributes<HTMLDivElement> {
	setIsOpened?: (opened: boolean) => void;
}

export const NotificationsPopup: FC<INotificationsPopupProps> = ({ className, setIsOpened, ...props }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { appendReadNotification, setRead } = useReadNotifications();

	const { data: notifications, isFetching } = useNotificationsListQuery({ limit: 5 });
	const { data: unread } = useNotificationsUnreadCountRetrieveQuery();
	const { clearInfinityScroll } = useCommonSlice();

	const onNavigate = (route: string) => {
		clearInfinityScroll();
		setIsOpened?.(false);
		navigate(route);
	};

	return (
		<div
			{...props}
			className={cn(className, styles.notificationsPopup, {
				[styles.withData]: !!notifications?.count,
			})}>
			<div className={styles.header}>
				<div className={styles.title}>
					<Typography.Title level={5}>{t('Notifications')}</Typography.Title>
					{!!unread?.unread_count && <Badge count={unread.unread_count} />}
				</div>
				<Button onClick={() => setRead({ ids: [] })} type={'text'}>
					{t('Mark all as read')}
				</Button>
			</div>

			<div className={styles.body}>
				{isFetching && <OverlayLoader />}

				{!!notifications?.count &&
					notifications?.results?.map((notification) => (
						<NotificationCard
							key={notification.id}
							notification={notification}
							appendReadNotification={appendReadNotification}
							size="s"
						/>
					))}

				{!notifications?.count && (
					<NoResult type="popup" onNavigate={() => onNavigate(`/${EMainRoutes.SOURCES}/${EMainRoutes.CREATE}`)} />
				)}
			</div>

			{!!notifications?.count && (
				<div className={styles.footer}>
					<Button type={'primary'} size={'large'} block onClick={() => onNavigate(`/${EMainRoutes.NOTIFICATIONS}`)}>
						{t('Show all notifications')}
					</Button>
				</div>
			)}
		</div>
	);
};
